/**
 * RKHDetailPage Component
 * Displays RKH summary data grouped by company with estate/division/block hierarchy
 * Shows division subtotals and estate totals in separate table rows
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Flex,
    VStack,
    Text,
    Spinner,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useToast,
    Card,
    CardBody,
    Heading,
    Alert,
    AlertIcon,
    AlertDescription,
    HStack,
    Image,
    IconButton,
    useBreakpointValue,
    Badge,
    Divider
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon, CheckIcon } from '@chakra-ui/icons';
import { FiEye} from 'react-icons/fi';
import angkongIcon from '../../../assets/icons/angkong_icon.svg';
import workerIcon from '../../../assets/icons/worker_icon.png';
import janjangIcon from '../../../assets/icons/janjang_icon.png';
import Navbar from '../../../components/Navbar';
import FilterRKHDetail from '../components/FilterRKHDetail';
import { useRKHSummary } from '../hooks/useRKHSummary';
import { RKHSummaryFilters, RKHCompanyGroup, RKHTotal, RKHBlock, RKHDivisionGroup } from '../types';
import { format } from 'date-fns';
import * as XLSX from 'xlsx-js-style';
import RKHDetailModal from '../components/RKHDetailModal';
import { useRKHDetail } from '../hooks/useRKHDetail';
import { trackDownload } from '../../../activity';

const formatNumber = (value: number): string => {
    return value.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatInteger = (value: number): string => {
    return value.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const RKHCompanySummary: React.FC<{ companyGroup: RKHCompanyGroup }> = ({ companyGroup }) => {
    const total = companyGroup.company_total;

    return (
        <Box borderRadius="lg" boxShadow="sm" mb={6} overflow="hidden" bg="white" border="1px" borderColor="gray.200">
            <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
                <Box bg="#e7efdf" p={4} flex="2.5" display="flex" alignItems="center">
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.600" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
                            Company
                        </Text>
                        <Text fontWeight="bold" fontSize="m">
                            {companyGroup.company}
                        </Text>
                    </VStack>
                </Box>
                <Box bg="white" p={4} flex="1" borderColor="gray.100" borderTop={{ base: "1px solid", lg: "none" }}>
                    <HStack spacing={3}>
                        <Box bg="#ededed" p={2} borderRadius="lg" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                            <Image src={janjangIcon} alt="Janjang Icon" boxSize="25px" objectFit="contain" />
                        </Box>
                        <Box>
                            <Text color="black"><b>Total Est. </b>Janjang</Text>
                            <Text color="black"><b>{formatInteger(total?.est_bunch ?? 0)}</b></Text>
                        </Box>
                    </HStack>
                </Box>
                <Box bg="white" p={4} flex="1" borderTop={{ base: "1px solid", lg: "none" }} borderColor="gray.100">
                    <HStack spacing={3}>
                        <Box bg="#ededed" p={2} borderRadius="lg" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                            <Image src={angkongIcon} alt="Berat Icon" boxSize="25px" objectFit="contain" />
                        </Box>
                        <Box>
                            <Text color="black"><b>Total Est. </b>Berat</Text>
                            <Text color="black"><b>{formatInteger(total?.est_weight ?? 0)} </b>kg</Text>
                        </Box>
                    </HStack>
                </Box>
                <Box bg="white" p={4} flex="1" borderTop={{ base: "1px solid", lg: "none" }} borderColor="gray.100">
                    <HStack spacing={3}>
                        <Box bg="#ededed" p={2} borderRadius="lg" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                            <Image src={workerIcon} alt="HK Icon" boxSize="25px" objectFit="contain" />
                        </Box>
                        <Box>
                            <Text color="black"><b>Total Est. </b>HK</Text>
                            <Text color="black"><b>{formatInteger(total?.est_hk ?? 0)}</b></Text>
                        </Box>
                    </HStack>
                </Box>
            </Flex>
        </Box>
    );
};

const RKHMobileTotal: React.FC<{ total: RKHTotal | null; division: string }> = ({ total, division }) => (
    <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200">
        <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">SUMMARY DIVISI {division}</Text>
        <VStack align="stretch" spacing={0}>
            <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                <Text fontSize="xs" fontWeight="bold" color="black">Total Luas HA:</Text>
                <Text fontSize="sm" fontWeight="bold" color="black">{formatNumber(total?.harvest_area ?? 0)}</Text>
            </HStack>
            <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                <Text fontSize="xs" fontWeight="bold" color="black">Total Target Luas HA:</Text>
                <Text fontSize="sm" fontWeight="bold" color="black">{formatNumber(total?.harvest_area_target ?? 0)}</Text>
            </HStack>
            <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                <Text fontSize="xs" fontWeight="bold" color="black">Total Est. Weight (kg):</Text>
                <Text fontSize="sm" fontWeight="bold" color="black">{formatInteger(total?.est_weight ?? 0)}</Text>
            </HStack>
            <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                <Text fontSize="xs" fontWeight="bold" color="black">Total Est. Bunch:</Text>
                <Text fontSize="sm" fontWeight="bold" color="black">{formatInteger(total?.est_bunch ?? 0)}</Text>
            </HStack>
            <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                <Text fontSize="xs" fontWeight="bold" color="black">Total Est. HK:</Text>
                <Text fontSize="sm" fontWeight="bold" color="black">{formatInteger(total?.est_hk ?? 0)}</Text>
            </HStack>
            <HStack justify="space-between" w="full" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                <Text fontSize="xs" fontWeight="bold" color="black">Total Est. Output:</Text>
                <Text fontSize="sm" fontWeight="bold" color="blue.700">{formatInteger(total?.est_output ?? 0)}</Text>
            </HStack>
        </VStack>
    </Box>
);

const RKHMobileCard: React.FC<{ block: RKHBlock; rkh_date: string }> = ({ block, rkh_date }) => {
    return (
        <Box>
            <VStack align="stretch" spacing={3}>
                <HStack justify="space-between" align="flex-start">
                    <VStack align="stretch" spacing={1}>
                        <HStack>
                            <Text fontSize="sm" fontWeight="medium" minW="80px">Tanggal:</Text>
                            <Text fontSize="sm" fontWeight="medium">{rkh_date ? format(new Date(rkh_date), 'dd/MM/yyyy') : '-'}</Text>
                        </HStack>
                        <HStack>
                            <Text fontSize="sm" fontWeight="medium" minW="80px">Kode Blok:</Text>
                            <Badge colorScheme="blue" fontSize="sm">{block.block_code || '-'}</Badge>
                        </HStack>
                        <HStack>
                            <Text fontSize="sm" fontWeight="medium" minW="80px">Carry Over:</Text>
                            {block.is_carry_over ? (
                                <CheckIcon boxSize={5} color="green.500" />
                            ) : (
                                <Text fontSize="sm" fontWeight="medium">-</Text>
                            )}
                        </HStack>
                    </VStack>
                </HStack>
                <Divider borderWidth="1px" borderColor="gray.200" />
                <VStack align="stretch" spacing={0}>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                        <Text fontSize="xs" color="gray.600">Luas HA:</Text>
                        <Text fontSize="sm" fontWeight="semibold" color="black">{formatNumber(block.harvest_area ?? 0)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                        <Text fontSize="xs" color="gray.600">Target Luas HA:</Text>
                        <Text fontSize="sm" fontWeight="semibold" color="black">{formatNumber(block.harvest_area_target ?? 0)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                        <Text fontSize="xs" color="gray.600">Total Ancak:</Text>
                        <Text fontSize="sm" fontWeight="semibold" color="black">{formatInteger(block.total_ancak ?? 0)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md" align="flex-start">
                        <Text fontSize="xs" color="gray.600">Ancaks:</Text>
                        <VStack align="end" spacing={0}>
                            {block.ancaks && block.ancaks.length > 0 ? block.ancaks.map((ancak, idx) => (
                                <Text key={idx} fontSize="sm" fontWeight="semibold" color="black">{ancak}</Text>
                            )) : <Text fontSize="sm" fontWeight="semibold" color="black">-</Text>}
                        </VStack>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                        <Text fontSize="xs" color="gray.600">AKP:</Text>
                        <Text fontSize="sm" fontWeight="semibold" color="black">{formatNumber(block.akp ?? 0)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                        <Text fontSize="xs" color="gray.600">Est. Weight (kg):</Text>
                        <Text fontSize="sm" fontWeight="semibold" color="black">{formatInteger(block.est_weight ?? 0)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                        <Text fontSize="xs" color="gray.600">Est. Bunch:</Text>
                        <Text fontSize="sm" fontWeight="semibold" color="black">{formatInteger(block.est_bunch ?? 0)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                        <Text fontSize="xs" color="gray.600">Est. HK:</Text>
                        <Text fontSize="sm" fontWeight="semibold" color="black">{formatInteger(block.est_hk ?? 0)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                        <Text fontSize="xs" color="gray.600">Est. Output:</Text>
                        <Text fontSize="sm" fontWeight="bold" color="blue.600">{formatInteger(block.est_output ?? 0)}</Text>
                    </HStack>
                </VStack>
            </VStack>
        </Box>
    );
};

const UnplannedBlocksSection: React.FC<{ divisions: RKHDivisionGroup[]; isMobile?: boolean }> = ({ divisions, isMobile = false }) => {
    const groupedBlocks: Record<string, { division: string; blocks: any[] }> = {};

    divisions.forEach(d => {
        if (d.unplanned_blocks && d.unplanned_blocks.length > 0) {
            if (!groupedBlocks[d.division]) {
                groupedBlocks[d.division] = {
                    division: d.division,
                    blocks: []
                };
            }
            d.unplanned_blocks.forEach(block => {
                if (!groupedBlocks[d.division].blocks.find(b => b.block_id === block.block_id)) {
                    groupedBlocks[d.division].blocks.push(block);
                }
            });
        }
    });

    const divisionList = Object.values(groupedBlocks);

    if (divisionList.length === 0) return null;

    return (
        <Box
            p={4}
            bg="white"
            borderTop={isMobile ? "none" : "1px solid"}
            borderTopColor="blackAlpha.300"
            borderLeft={isMobile ? "4px solid" : "none"}
            borderLeftColor="yellow.400"
            borderRadius={isMobile ? "md" : "none"}
            boxShadow={isMobile ? "sm" : "none"}
        >
            <Text fontSize="xs" color="black" mb={2}>Blok AKP yang tidak masuk RKH:</Text>
            <VStack align="stretch" spacing={1}>
                {divisionList.map((item, idx) => (
                    <HStack key={idx} align="flex-start" spacing={3}>
                        <Text fontSize="2xs" color="gray.900" minW="80px">
                            {item.division}:
                        </Text>
                        <Text fontSize="2xs" color="gray.900">
                            {item.blocks.map(b => b.block_code).join(', ')}
                        </Text>
                    </HStack>
                ))}
            </VStack>
        </Box>
    );
};
const RKHDetailPage: React.FC = () => {
    const toast = useToast();
    const { data, loading, error, fetchData } = useRKHSummary();
    const isMobile = useBreakpointValue({ base: true, lg: false });
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    const {
        data: detailData,
        loading: detailLoading,
        error: detailError,
        fetchDetail,
        clearDetail
    } = useRKHDetail();
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    const handleViewDetail = useCallback(async (rkhId?: string) => {
        if (!rkhId) {
            toast({
                title: 'Error',
                description: 'RKH ID tidak ditemukan untuk data ini',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        setIsDetailModalOpen(true);
        await fetchDetail(rkhId);
    }, [fetchDetail, toast]);

    const handleCloseDetailModal = useCallback(() => {
        setIsDetailModalOpen(false);
        clearDetail();
    }, [clearDetail]);

    // Get default date range (Today)
    const getDefaultDateRange = () => {
        const today = new Date();

        return {
            date_from: format(today, 'yyyy-MM-dd'),
            date_to: format(today, 'yyyy-MM-dd'),
        };
    };

    const [filters, setFilters] = useState<RKHSummaryFilters>({
        ...getDefaultDateRange(),
        company_id: null,
        estate_id: null,
        division_id: null,
    });

    const handleFilterChange = useCallback((newFilters: RKHSummaryFilters) => {
        setFilters(newFilters);
    }, []);

    const handleApplyFilters = useCallback((newFilters: RKHSummaryFilters) => {
        fetchData(newFilters);
    }, [fetchData]);

    useEffect(() => {
        fetchData(filters);
    }, []);

    const handleClearFilters = useCallback(() => {
        const defaults = getDefaultDateRange();
        const clearedFilters: RKHSummaryFilters = {
            date_from: defaults.date_from,
            date_to: defaults.date_to,
            company_id: null,
            estate_id: null,
            division_id: null,
        };
        setFilters(clearedFilters);
        fetchData(clearedFilters);
    }, [fetchData]);

    const handleExportToExcel = useCallback(() => {
        if (!data || data.length === 0) {
            toast({
                title: 'No data to export',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            const workbook = XLSX.utils.book_new();

            data.forEach((companyGroup) => {
                const sheetData: any[] = [];

                // Header
                sheetData.push([
                    'Company',
                    'Date',
                    'Estate',
                    'Divisi',
                    'Blok',
                    'Carry Over',
                    'Total Ancak',
                    'Ancaks',
                    'AKP',
                    'Est. Weight (kg)',
                    'Est. Bunch',
                    'Est. HK',
                    'Est. Output',
                ]);

                // Data rows
                companyGroup.estates.forEach(estate => {
                    estate.divisions.forEach(div => {
                        div.blocks.forEach(block => {
                            sheetData.push([
                                companyGroup.company || '',
                                div.rkh_date || '',
                                estate.estate || '',
                                div.division || '',
                                block.block_code || '',
                                block.is_carry_over ? '✓' : '-',
                                block.total_ancak || 0,
                                block.ancaks?.join(', ') || '-',
                                block.akp || 0,
                                block.est_weight,
                                block.est_bunch,
                                block.est_hk,
                                block.est_output,
                            ]);
                        });
                    });
                });

                const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
                const safeName = (companyGroup.company || 'Company').substring(0, 31).replace(/[[\]*?/\\:]/gi, '');
                XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
            });

            XLSX.writeFile(workbook, `RKH_Detail_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
            trackDownload('RKH Detail');

            toast({
                title: 'Export successful',
                description: 'Data exported to Excel',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (err: any) {
            toast({
                title: 'Export failed',
                description: err.message || 'Failed to export data',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    }, [data, toast]);

    useEffect(() => {
        if (error) {
            toast({
                title: 'Error',
                description: error,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    }, [error, toast]);

    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    return (
        <Flex direction="column" minH="100vh" bg="gray.50">
            <Navbar />

            <Box flex="1" p={{ base: 4, md: 6 }}>
                <VStack spacing={6} align="stretch">
                    {/* Header */}
                    <Heading size="lg" color="customGreen.500">
                        RKH Summary
                    </Heading>

                    {/* Filters */}
                    <FilterRKHDetail
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                        onApplyFilters={handleApplyFilters}
                        onExport={handleExportToExcel}
                        onRefresh={() => fetchData(filters)}
                    />

                    {/* Loading State */}
                    {loading && (
                        <Flex justify="center" align="center" minH="200px">
                            <Spinner size="xl" color="customGreen.500" thickness="4px" />
                        </Flex>
                    )}

                    {/* Error State */}
                    {!loading && error && (
                        <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {/* Empty State */}
                    {!loading && !error && data && data.length === 0 && (
                        <Card>
                            <CardBody>
                                <Flex justify="center" align="center" minH="200px">
                                    <Text color="gray.500">
                                        Tidak ada data. Gunakan filter untuk mencari data.
                                    </Text>
                                </Flex>
                            </CardBody>
                        </Card>
                    )}

                    {/* Data Tables - Grouped View */}
                    {!loading && !error && data && data.length > 0 && (
                        <>
                            {isMobile ? (
                                <VStack spacing={6} align="stretch">
                                    {data.map((companyGroup) => (
                                        <VStack key={companyGroup.company_id} align="stretch" spacing={3}>
                                            <RKHCompanySummary companyGroup={companyGroup} />
                                            {companyGroup.estates.map((estate) => (
                                                <VStack key={estate.estate_id} align="stretch" spacing={3}>
                                                    {estate.divisions.map((group) => {
                                                        const groupId = `${companyGroup.company_id}-${estate.estate_id}-${group.division_id}`;
                                                        return (
                                                            <VStack key={group.division_id} align="stretch" spacing={3}>
                                                                <HStack>
                                                                    <Text fontWeight="bold" color="blue.700" fontSize="md" pl={1} flex={1}>
                                                                        {group.division} ({estate.estate})
                                                                    </Text>
                                                                    <IconButton
                                                                        aria-label="Lihat detail"
                                                                        icon={<FiEye />}
                                                                        size="sm"
                                                                        colorScheme="blue"
                                                                        variant="ghost"
                                                                        onClick={() => handleViewDetail(group.rkh_id)}
                                                                    />
                                                                    <IconButton
                                                                        aria-label="Expand division"
                                                                        icon={expandedGroups[groupId] ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() => toggleGroup(groupId)}
                                                                    />
                                                                </HStack>
                                                                <RKHMobileTotal total={group.division_total} division={group.division} />
                                                                {expandedGroups[groupId] && (
                                                                    <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200" overflow="hidden">
                                                                        <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.200" />}>
                                                                            {group.blocks.map((block) => (
                                                                                <Box p={4} key={block.block_id}>
                                                                                    <RKHMobileCard block={block} rkh_date={group.rkh_date} />
                                                                                </Box>
                                                                            ))}
                                                                        </VStack>
                                                                    </Box>
                                                                )}
                                                            </VStack>
                                                        );
                                                    })}
                                                    <UnplannedBlocksSection divisions={estate.divisions} isMobile={true} />
                                                </VStack>
                                            ))}
                                        </VStack>
                                    ))}
                                </VStack>
                            ) : (
                                <VStack spacing={6} align="stretch">
                                    {data.map((companyGroup) => (
                                        <React.Fragment key={companyGroup.company_id}>
                                            {/* Company Summary Card */}
                                            <RKHCompanySummary companyGroup={companyGroup} />
                                            {companyGroup.estates.map((estate) => {
                                                const isAnyGroupInThisEstateExpanded = estate.divisions.some(div => expandedGroups[`${companyGroup.company_id}-${estate.estate_id}-${div.division_id}`]);

                                                return (
                                                    <Box key={estate.estate_id} bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.200" overflow="hidden">
                                                        <Box overflowX="auto">
                                                            <Table variant="simple" size="sm">
                                                                <Thead bg="gray.200">
                                                                    <Tr bg="#4A5A33">
                                                                        {isAnyGroupInThisEstateExpanded ? (
                                                                            <>
                                                                                <Th color="white" py={2}>Tanggal</Th>
                                                                                <Th color="white" py={2}>Blok</Th>
                                                                                <Th color="white" py={2}>Carry Over</Th>
                                                                            </>
                                                                        ) : (
                                                                            <Th py={2}></Th>
                                                                        )}
                                                                        <Th isNumeric color="white" py={2}>Luas HA</Th>
                                                                        <Th isNumeric color="white" py={2}>Target Luas HA</Th>
                                                                        {isAnyGroupInThisEstateExpanded && (
                                                                            <>
                                                                                <Th isNumeric color="white" py={2}>Total Ancak</Th>
                                                                                <Th color="white" py={2}>Ancak</Th>
                                                                                <Th isNumeric color="white" py={2}>AKP</Th>
                                                                            </>
                                                                        )}
                                                                        <Th isNumeric color="white" py={2}>Est. Weight (kg)</Th>
                                                                        <Th isNumeric color="white" py={2}>Est. Bunch</Th>
                                                                        <Th isNumeric color="white" py={2}>Est. HK</Th>
                                                                        <Th isNumeric color="white" py={2}>Est. Output</Th>
                                                                        <Th color="white" textAlign="center" py={2}>Details</Th>
                                                                    </Tr>
                                                                    {/* Estate Total Row */}
                                                                    <Tr bg="#e7efdf">
                                                                        <Td colSpan={isAnyGroupInThisEstateExpanded ? 3 : 1} py={2} pr={4}>
                                                                            <VStack align="start" spacing={0}>
                                                                                <Text fontWeight="bold" color="gray.800" fontSize="xs">
                                                                                    {estate.estate}
                                                                                </Text>
                                                                                <Text fontWeight="bold" color="gray.600" fontSize="xs">
                                                                                    TOTAL
                                                                                </Text>
                                                                            </VStack>
                                                                        </Td>
                                                                        <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber(estate.estate_total?.harvest_area ?? 0)}</Td>
                                                                        <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber(estate.estate_total?.harvest_area_target ?? 0)}</Td>
                                                                        {isAnyGroupInThisEstateExpanded && (
                                                                            <>
                                                                                <Td></Td>
                                                                                <Td></Td>
                                                                                <Td></Td>
                                                                            </>
                                                                        )}
                                                                        <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(estate.estate_total?.est_weight ?? 0)}</Td>
                                                                        <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(estate.estate_total?.est_bunch ?? 0)}</Td>
                                                                        <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(estate.estate_total?.est_hk ?? 0)}</Td>
                                                                        <Td fontSize="xs" isNumeric color="blue.700" fontWeight="bold">{formatInteger(estate.estate_total?.est_output ?? 0)}</Td>
                                                                        <Td></Td>
                                                                    </Tr>
                                                                </Thead>
                                                                <Tbody>
                                                                    {estate.divisions.map((group) => {
                                                                        const groupId = `${companyGroup.company_id}-${estate.estate_id}-${group.division_id}`;
                                                                        return (
                                                                            <React.Fragment key={groupId}>
                                                                                {/* Division Header Row */}
                                                                                <Tr _hover={{ bg: "blue.50" }}>
                                                                                    <Td colSpan={isAnyGroupInThisEstateExpanded ? 3 : 1} py={2} pr={4}>
                                                                                        <HStack spacing={2}>
                                                                                            <Text fontWeight="bold" color="blue.800" fontSize="xs">
                                                                                                {group.division}
                                                                                            </Text>
                                                                                            <IconButton
                                                                                                aria-label='Expand group'
                                                                                                icon={expandedGroups[groupId] ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                                                                                size="xs"
                                                                                                variant="ghost"
                                                                                                onClick={() => toggleGroup(groupId)}
                                                                                            />
                                                                                        </HStack>
                                                                                    </Td>
                                                                                    <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber(group.division_total?.harvest_area ?? 0)}</Td>
                                                                                    <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber(group.division_total?.harvest_area_target ?? 0)}</Td>
                                                                                    {isAnyGroupInThisEstateExpanded && (
                                                                                        <>
                                                                                            <Td></Td>
                                                                                            <Td></Td>
                                                                                            <Td></Td>
                                                                                        </>
                                                                                    )}
                                                                                    <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(group.division_total?.est_weight ?? 0)}</Td>
                                                                                    <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(group.division_total?.est_bunch ?? 0)}</Td>
                                                                                    <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(group.division_total?.est_hk ?? 0)}</Td>
                                                                                    <Td fontSize="xs" isNumeric color="blue.700" fontWeight="bold">{formatInteger(group.division_total?.est_output ?? 0)}</Td>
                                                                                    <Td textAlign="center">
                                                                                        <IconButton
                                                                                            aria-label="Lihat detail"
                                                                                            icon={<FiEye />}
                                                                                            size="sm"
                                                                                            colorScheme="blue"
                                                                                            variant="ghost"
                                                                                            onClick={() => handleViewDetail(group.rkh_id)}
                                                                                        />
                                                                                    </Td>
                                                                                </Tr>

                                                                                {/* Division Blocks */}
                                                                                {expandedGroups[groupId] && group.blocks.map((block) => (
                                                                                    <Tr key={block.block_id} _hover={{ bg: 'blue.50' }}>
                                                                                        <Td fontSize="xs">{group.rkh_date ? format(new Date(group.rkh_date), 'dd/MM/yyyy') : '-'}</Td>
                                                                                        <Td fontSize="xs">{block.block_code || '-'}</Td>
                                                                                        <Td fontSize="xs">
                                                                                            {block.is_carry_over ? (
                                                                                                <CheckIcon boxSize={4} color="green.500" />
                                                                                            ) : (
                                                                                                <Text>-</Text>
                                                                                            )}
                                                                                        </Td>
                                                                                        <Td fontSize="xs" isNumeric>{formatNumber(block.harvest_area ?? 0)}</Td>
                                                                                        <Td fontSize="xs" isNumeric>{formatNumber(block.harvest_area_target ?? 0)}</Td>
                                                                                        <Td fontSize="xs" isNumeric>{formatInteger(block.total_ancak ?? 0)}</Td>
                                                                                        <Td fontSize="xs">
                                                                                            <VStack align="start" spacing={0}>
                                                                                                {block.ancaks && block.ancaks.length > 0 ? block.ancaks.map((ancak, idx) => (
                                                                                                    <Text key={idx} whiteSpace="nowrap">{ancak}</Text>
                                                                                                )) : <Text>-</Text>}
                                                                                            </VStack>
                                                                                        </Td>
                                                                                        <Td fontSize="xs" isNumeric>{formatNumber(block.akp ?? 0)}</Td>
                                                                                        <Td fontSize="xs" isNumeric>{formatInteger(block.est_weight ?? 0)}</Td>
                                                                                        <Td fontSize="xs" isNumeric>{formatInteger(block.est_bunch ?? 0)}</Td>
                                                                                        <Td fontSize="xs" isNumeric>{formatInteger(block.est_hk ?? 0)}</Td>
                                                                                        <Td fontSize="xs" isNumeric color="blue.600">{formatInteger(block.est_output ?? 0)}</Td>
                                                                                    </Tr>
                                                                                ))}
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </Tbody>
                                                            </Table>
                                                        </Box>
                                                        <UnplannedBlocksSection divisions={estate.divisions} />
                                                    </Box>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                                </VStack>
                            )}
                        </>
                    )}
                </VStack>
            </Box>

            {/* Detail Modal */}
            <RKHDetailModal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                data={detailData}
                loading={detailLoading}
                error={detailError}
            />
        </Flex>
    );
};

export default RKHDetailPage;
